import { createSearchParams, useNavigate } from "react-router-dom";
import { GovType, MapType } from "../types/dataTypes"
import { BASE_LAYERS, LAYER_IDS, MAP_BOUNDS, MAP_CENTRE, MAP_PADDING } from "./mapConsts";
import { searchParamsObj, toSlug } from "./dataHandler";
import path from "path";
import maplibregl, { MapGeoJSONFeature, Padding } from "maplibre-gl";
import * as turf from "@turf/turf";

export function mapGetLayerExists(mapCurrent: MapType["current"], layerId: string) {
    return mapCurrent?.getLayer(layerId);
}

export function mapGetSourceExists(mapCurrent: MapType["current"], layerId: string) {
    return mapCurrent?.getSource(layerId);
}

// VISIBILITY

export function mapVisibilityNone(map: MapType, layerId: string) {
    map.current?.setLayoutProperty(layerId, 'visibility', 'none');
}

export function mapVisibilityVisible(map: MapType, layerId: string) {
    map.current?.setLayoutProperty(layerId, 'visibility', 'visible');
}

export function mapCursorPointer(map: MapType) {
    if (!map.current) return
    map.current.getCanvas().style.cursor = 'pointer';
}

export function mapCursorNormal(map: MapType) {
    if (!map.current) return
    map.current.getCanvas().style.cursor = '';
}

// REMOVE LAYER AND SOURCE
export function mapRemoveLayer(mapCurrent: MapType["current"], layerIdArray: string[]) {
    if (!mapCurrent) return;
    layerIdArray.forEach((layerId) => {
        if (mapGetLayerExists(mapCurrent, layerId)) { mapCurrent.removeLayer(layerId) } 
    }); 
}

export function mapRemoveSource(mapCurrent: MapType["current"], layerIdArray: string[]) {
    if (!mapCurrent) return;
    layerIdArray.forEach((layerId) => {
        if (mapGetSourceExists(mapCurrent, layerId)) { mapCurrent.removeSource(layerId) } 
    }); 
}

export function mapRemoveLayerSource(mapCurrent: MapType["current"], layerIdArray: string[]) {
    if (!mapCurrent) return;
    mapRemoveLayer(mapCurrent, layerIdArray);
    mapRemoveSource(mapCurrent, layerIdArray)
}

export function mapCorrectLayerOrder(mapCurrent: MapType["current"], layerIdArray: string[]) {
    if (!mapCurrent) return;
    layerIdArray.forEach((layerId: string, i) => {
        if(mapGetLayerExists(mapCurrent, layerId)){
            for (let n=1; n < (layerIdArray.length - i); n++){
                if(mapGetLayerExists(mapCurrent, layerIdArray[i+n])){
                    mapCurrent.moveLayer(layerIdArray[i+n], layerId);
                    n=layerIdArray.length;
                }
            }
        }
    });
}

//Handle click event to navigate to the selected government
export function handleClickGov(govName: string, map: MapType) {
    const mapCurrent = map.current;
   
    //Clear the selected state of any previously selected feature
    if (mapCurrent) {
      mapCurrent.queryRenderedFeatures({ layers: [LAYER_IDS.GOVS_LINE] }).forEach((feature) => {
        mapCurrent.setFeatureState(
          { source: LAYER_IDS.GOVS_LINE, id: feature.id },
          { selected: false }
        );
      });
    }
    
    // Query the selected government feature
    const selectedFeature = mapCurrent?.querySourceFeatures(LAYER_IDS.GOVS_LINE, {
      sourceLayer: LAYER_IDS.GOVS_LINE,
      filter: ["==", "ADM1_EN", govName],
    }) as MapGeoJSONFeature[];

    // Set the selected state of the clicked feature to true
    if (mapCurrent && selectedFeature) {
      mapCurrent.setFeatureState(
        { source: LAYER_IDS.GOVS_LINE, id: selectedFeature[0].id },
        { selected: true }
      );
    }

    // Fly to the selected government    
    if (selectedFeature){
        const bbox = turf.bbox(selectedFeature[0]) as [number, number, number, number];
        mapCurrent?.fitBounds(bbox, {zoom: 9});
    }
  }


  // Function to convert point to a small square polygon for 3D bar visualization
export function pointToPolygon (point: any, size = 0.05){
    const [x, y] = point;
    return [
      [x - size, y - size],
      [x + size, y - size],
      [x + size, y + size],
      [x - size, y + size],
      [x - size, y - size], // Closing the polygon
    ];
  };
  
  // Function to calculate quantiles
export function calculateQuantiles(data: any[], quantiles: any){
    data.sort((a: any, b: any) => a - b);
    return quantiles.map((q:any) => {
      const pos = (data.length - 1) * q;
      const base = Math.floor(pos);
      const rest = pos - base;
      return data[base + 1] !== undefined
        ? Number((data[base] + rest * (data[base + 1] - data[base])).toFixed(2))
        : Number(data[base].toFixed(2));
    });
  };

  export function resetMap(map: MapType, navigate: ReturnType<typeof useNavigate>) {
    const mapCurrent = map.current;
    // Logic to reset the map to its initial state
    if (mapCurrent) {
      mapCurrent.fitBounds(MAP_BOUNDS, {
        zoom: 8,
        padding: MAP_PADDING,
        pitch: 0,
        bearing: 0,
        duration: 1000, // duration of the animation in milliseconds
        easing: (t) => t * (2 - t), // easing function for the animation
      });
    }
  
    // Reset the URL search parameter 'gov'
    navigate({
      pathname: window.location.pathname,
      search: "",
    }, { replace: true });
  }
